import { AddIcon, BellIcon, HamburgerIcon } from "../icons/btnIcons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useState, useRef } from "react";
import { Dropdown, Image, Nav } from "react-bootstrap";
import IconBtn from "../ui/button/IconBtn";
import { useLoader } from "../helpers/contexts/loaderContext";
import { useRolePermission } from "../hooks/useRolePermission";
import Cookies from "js-cookie";
import GlobalSearch from "../components/GlobalSearch";
import {
  HomeIcon,
  ProductsIcon,
  QuotationIcon,
  SettingsIcon,
  UsersIcon,
  BagIcon,
  FollowUpIcon,
  BuildingIcon,
  LocationIcon,
  ReportChartIcon,
  CheckIcon,
} from "../icons/otherIcons";
import { Avatar } from "../ui/details-header/DetailsHeader";
import { PageSkeleton } from "../ui/details-header/DetailsSkeleton";
import { reqToLogout, resetAuthState, reqToSwitchOrganization } from "../redux/slice/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../helpers/contexts/ToastContext";
import { handleApiError } from "../utility/handleApiError";
import { fetchNotifications } from "../redux/slice/notificationSlice";
import CustomDropdown from "../ui/dropdowns/CustomDropdown";
import FollowUpModal from "../modals/follow-up/FollowUpModal";
import NotificationPanel from "../modules/notifications/NotificationPanel";
import CustomerModal from "../modals/customer/CustomerModal";
import { EyeIcon } from "../icons/inputIcons";

const navItems = [
  {
    title: "Home",
    items: [
      {
        eventKey: "dashboard",
        title: "Dashboard",
        href: "/",
        icon: <HomeIcon />,
      },
    ],
  },
  {
    title: "Sales",
    items: [
      {
        eventKey: "customer",
        title: "Customer",
        href: "/customer",
        icon: <BuildingIcon />,
        module: "customers",
      },
      {
        eventKey: "lead",
        title: "Lead",
        href: "/lead",
        icon: <BagIcon />,
        module: "leads",
      },
      {
        eventKey: "quotation",
        title: "Quotation",
        href: "/quotation",
        icon: <QuotationIcon />,
        module: "quotations",
      },
      {
        eventKey: "follow-ups",
        title: "Follow-ups",
        href: "/follow-ups",
        icon: <FollowUpIcon />,
        module: "followups",
      },
      {
        eventKey: "reports",
        title: "Reports & Analytics",
        href: "/reports",
        icon: <ReportChartIcon />,
        module: "reports",
      },
    ],
  },
  {
    title: "Management",
    items: [
      {
        eventKey: "products",
        title: "Products",
        href: "/products",
        icon: <ProductsIcon />,
        module: "products",
        subItems: [
          {
            eventKey: "all-products",
            title: "All Products",
            href: "/products",
          },
          {
            eventKey: "product-packages",
            title: "Products Packages",
            href: "/products/packages",
          },
          {
            eventKey: "product-settings",
            title: "Product Settings",
            href: "/products/product-settings",
          },
        ],
      },
      {
        eventKey: "user-roles",
        title: "User & Roles",
        href: "/user-roles",
        icon: <UsersIcon />,
        module: "userprofile",
        subItems: [
          { eventKey: "users", title: "Users", href: "/user-roles" },
          { eventKey: "roles", title: "Roles", href: "/user-roles/roles" },
          {
            eventKey: "sales-targets",
            title: "Sales Targets",
            href: "/user-roles/sales-targets",
            // Setting targets needs Reports create / edit / delete, not just viewing reports
            module: "reports",
            actions: ["create", "edit", "delete"],
          },
        ],
      },
      {
        eventKey: "tracking",
        title: "GPS Tracking",
        href: "/tracking",
        icon: <LocationIcon />,
        module: "tracking",
      },
      {
        eventKey: "spy",
        title: "Monitoring",
        href: "/monitoring",
        icon: <EyeIcon size={16} />,
        superAdminOnly: true,
      },
      {
        eventKey: "settings",
        title: "Settings",
        href: "/settings",
        icon: <SettingsIcon />,
        module: "settings",
      },
    ],
  },
];

const Layout = ({ children }) => {
  const location = useLocation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { setLoading } = useLoader();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [isSwitchingOrg, setIsSwitchingOrg] = useState(false);
  const { hasPermission } = useRolePermission();
  const { totalUnread } = useSelector((state) => state.notifications);
  const unreadCount = totalUnread;

  // Filter navigation items based on 'view' permission and superadmin restriction
  const filteredNavItems = navItems
    .map((section) => ({
      ...section,
      items: section.items
        .filter((item) => {
          if (item.superAdminOnly) {
            return !!user?.is_superadmin;
          }
          return !item.module || hasPermission(item.module, "view");
        })
        .map((item) =>
          item.subItems
            ? {
                ...item,
                subItems: item.subItems.filter(
                  (sub) =>
                    !sub.actions || sub.actions.some((action) => hasPermission(sub.module, action))
                ),
              }
            : item
        ),
    }))
    .filter((section) => section.items.length > 0);

  // Quick Create options filtered by 'create' permission
  const quickCreateOptions = [
    { label: "Create Lead", href: "/lead/create", module: "leads" },
    {
      label: "Create Quotation",
      href: "/quotation/create",
      module: "quotations",
    },
    {
      label: "Create FollowUp",
      onClick: () => setActiveModal("followup"),
      module: "followups",
    },
    {
      label: "Create Customer",
      onClick: () => setActiveModal("customer"),
      module: "customers",
    },
  ].filter((option) => !option.module || hasPermission(option.module, "create"));

  const isParentPath = (href) => {
    // Standard match: exact path or child path
    if (currentPath === href || currentPath.startsWith(href + "/")) return true;
    // The other dashboards (/dashboard/leads, …) open from the switch on the dashboard page
    if (href === "/" && currentPath.startsWith("/dashboard/")) return true;
    // When admin views a user's profile (/profile?userId=...), mark "User & Roles" as active
    if (href === "/user-roles" && currentPath === "/profile") return true;
    return false;
  };

  const getActiveSubItemHref = (item) => {
    const subs = item.subItems || [];
    if (!subs.length) return null;
    // A user's profile opened by an admin belongs to Users, a role's permissions to Roles
    if (item.href === "/user-roles" && currentPath === "/profile") return "/user-roles";
    if (item.href === "/user-roles" && currentPath === "/user-roles/permissions")
      return "/user-roles/roles";
    const exact = subs.find((s) => currentPath === s.href);
    if (exact) return exact.href;
    const prefixes = subs.filter((s) => currentPath.startsWith(s.href + "/"));
    if (!prefixes.length) return null;
    prefixes.sort((a, b) => b.href.length - a.href.length);
    return prefixes[0].href;
  };

  const isAnySubItemActive = (item) => !!getActiveSubItemHref(item);

  const handleLogout = async () => {
    setLoading(true);
    try {
      const response = await dispatch(reqToLogout()).unwrap();

      dispatch(resetAuthState());
      Cookies.remove("login_token");

      showToast(response?.message || "Logged out successfully!", "success");

      localStorage.setItem(
        "crm_logged_out_message",
        response?.message || "Logged out successfully!"
      );

      navigate("/login");
    } catch (error) {
      handleApiError(error, { showToast });
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchOrganization = async (orgId) => {
    setIsSwitchingOrg(true);
    try {
      // Instantly navigate to dashboard to prevent active detail page components
      // from remounting and requesting old IDs under the new organization context.
      navigate("/", { replace: true });

      const res = await dispatch(reqToSwitchOrganization(orgId)).unwrap();
      showToast(res?.message || "Organization switched successfully!", "success");
    } catch (error) {
      handleApiError(error, { showToast });
    } finally {
      setIsSwitchingOrg(false);
    }
  };

  // Organisations you can switch to: initials + name, a tick on the one you're in (listed first)
  const organizationOptions = user?.organizations
    ?.filter((org) => org.pivot?.is_active !== false)
    ?.map((org) => ({ org, isCurrent: org.id === user.current_organization_id }))
    ?.sort((a, b) => Number(b.isCurrent) - Number(a.isCurrent))
    ?.map(({ org, isCurrent }) => ({
      label: (
        <span className="d-flex align-items-center gap-4 w-100 min-w-0">
          <Avatar name={org.name} size={28} />
          <span className="flex-column min-w-0">
            <span className="fs-sm text-truncate">{org.name}</span>
            {isCurrent && <span className="fs-xxs text-secondary">You're working here</span>}
          </span>
          {isCurrent && <CheckIcon size={16} className="ms-auto flex-shrink-0 text-primary" />}
        </span>
      ),
      active: isCurrent,
      onClick: isCurrent ? undefined : () => handleSwitchOrganization(org.id),
      as: "button",
      className: "rounded-2 py-3",
    }));

  useEffect(() => {
    setOpenDropdown(null);
    setIsNotificationOpen(false);
  }, [currentPath]);

  const prevUnreadCount = useRef(unreadCount);

  useEffect(() => {
    // Initial fetch on mount (latest 10 + unread count; the bell panel loads more on scroll)
    dispatch(fetchNotifications({ page: 1, limit: 10 }));

    // Poll for new notifications every 30 seconds for faster updates
    const interval = setInterval(() => {
      dispatch(fetchNotifications({ page: 1, limit: 10, force: true }));
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    if (unreadCount > prevUnreadCount.current) {
      showToast(
        `You have ${
          unreadCount - prevUnreadCount.current > 1 ? unreadCount - prevUnreadCount.current : "1"
        } new notification${unreadCount - prevUnreadCount.current > 1 ? "s" : ""}`,
        "primary"
      );
    }
    prevUnreadCount.current = unreadCount;
  }, [unreadCount, showToast]);

  return (
    <div
      className={`grid gap-0 vh-100 bg-light ${isCollapsed ? "sidebar-collapsed" : ""}`}
      style={{
        display: "grid",
        gridTemplateColumns: isCollapsed ? "76px 1fr" : "250px 1fr",
        transition: "grid-template-columns 0.5s ease",
      }}
    >
      {/* sidebar */}
      <aside className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
        <div
          className={`sticky-top py-10 border-end bg-white vh-100 d-flex flex-column ${
            isCollapsed ? "px-0 overflow-visible" : "ps-8 pe-4"
          }`}
        >
          <div className="text-center mb-14 logo-container">
            <Link to="/">
              <Image
                src={isCollapsed ? "/assets/logo-sm.png" : "/assets/logo.png"}
                alt="Logo"
                height={isCollapsed ? 32 : 58}
                width={isCollapsed ? 32 : 87}
                className="object-fit-contain"
              />
            </Link>
          </div>
          <ul
            className={`list-unstyled flex-grow-1 m-0 sidebar-nav-list ${isCollapsed ? "" : "overflow-auto custom-scroll pe-4"}`}
          >
            {filteredNavItems?.map((section, i) => (
              <li key={i} className="mb-8">
                {!isCollapsed && (
                  <span className="fs-xs fw-medium text-muted px-2 mb-2 d-block">
                    {section?.title}
                  </span>
                )}
                <Nav variant="sidebar" activeKey={currentPath} className="mt-8">
                  {section?.items?.map((item, j) => {
                    if (item?.subItems) {
                      return (
                        <Dropdown
                          key={j}
                          autoClose={false}
                          drop={isCollapsed ? "end" : "down"}
                          className={`nav-item ${isCollapsed ? "collapsed-dropdown" : ""}`}
                          show={
                            !isCollapsed
                              ? openDropdown === j || isAnySubItemActive(item)
                              : undefined
                          }
                          onToggle={(isOpen) => {
                            if (isCollapsed) return;
                            setOpenDropdown(isOpen ? j : null);
                          }}
                        >
                          <Dropdown.Toggle
                            as={Nav.Link}
                            active={isAnySubItemActive(item)}
                            className={`${isCollapsed ? "d-flex justify-content-center p-0 mx-auto position-relative ratio-1" : "gap-2"}`}
                          >
                            <i className="flex-center ratio-1 icon">{item.icon}</i>
                            {!isCollapsed ? (
                              <span>{item.title}</span>
                            ) : (
                              <span className="hover-text">{item.title}</span>
                            )}
                          </Dropdown.Toggle>
                          <Dropdown.Menu
                            className={
                              isCollapsed
                                ? "collapsed-menu shadow-sm border p-2"
                                : "sidebar-dropdown-menu"
                            }
                          >
                            {isCollapsed && (
                              <Dropdown.Header className="fs-xs fw-bold text-primary border-bottom mb-2 px-2">
                                {item.title}
                              </Dropdown.Header>
                            )}
                            {item?.subItems?.map((subItem, k) => (
                              <Dropdown.Item
                                key={k}
                                as={Link}
                                to={subItem?.href}
                                active={getActiveSubItemHref(item) === subItem.href}
                                className="fw-semibold rounded-2 py-2 px-3"
                                onClick={() => isCollapsed && setOpenDropdown(null)}
                              >
                                <span> {subItem?.title}</span>
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      );
                    }

                    return (
                      <Nav.Item key={j}>
                        <Nav.Link
                          as={Link}
                          to={item.href}
                          active={isParentPath(item.href)}
                          className={`${isCollapsed ? "d-flex justify-content-center p-0 mx-auto position-relative ratio-1" : "gap-2"}`}
                        >
                          <i className="flex-center ratio-1 icon">
                            {React.cloneElement(item.icon, {
                              size: isCollapsed ? 20 : 16,
                            })}
                          </i>
                          {!isCollapsed ? (
                            <span>{item?.title}</span>
                          ) : (
                            <span className="hover-text">{item?.title}</span>
                          )}
                        </Nav.Link>
                      </Nav.Item>
                    );
                  })}
                </Nav>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <main className="position-relative bg-light flex-column">
        {/* header */}
        <header className="py-5 px-12 border-bottom bg-white flex-between">
          <div className="d-flex align-items-center gap-12">
            <IconBtn
              variant="outline-secondary"
              icon={<HamburgerIcon size={18} />}
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-4 border-0 shadow-none"
            />
            <GlobalSearch />
          </div>
          <div className="d-flex align-items-center gap-12">
            {/* ─── Organization Switcher ─── */}
            {user?.organizations?.length > 1 && (
              <CustomDropdown
                id="org-switcher-dropdown"
                dropdownProps={{ align: "end" }}
                toggleProps={{
                  className:
                    "d-flex align-items-center gap-2 border-0 bg-transparent shadow-none p-2 lh-sm org-switcher-toggle",
                  children: (
                    <>
                      <BuildingIcon size={18} className="text-primary" />
                      <div className="text-start d-none d-md-block">
                        <span className="text-muted fs-xxs d-block">Organization</span>
                        <span className="fw-semibold text-primary ">
                          {user?.current_organization?.name || "Select Organization"}
                        </span>
                      </div>
                    </>
                  ),
                }}
                menuProps={{
                  className: "shadow-sm dropdown-menu-end border p-2 z-1022 org-switcher-menu",
                }}
                header="Switch organization"
                options={organizationOptions}
              />
            )}

            {quickCreateOptions.length > 0 && (
              <CustomDropdown
                id="quick-create-dropdown"
                as={IconBtn}
                toggleProps={{
                  icon: <AddIcon size={16} />,
                  className: "p-4 ",
                }}
                menuProps={{
                  className: "p-3 z-1022",
                }}
                itemProps={{ className: "rounded-2" }}
                options={quickCreateOptions}
              />
            )}

            {/* ─── Notification Bell ─── */}
            <div className="position-relative">
              <IconBtn
                variant="outline-primary"
                id="notification-bell-btn"
                className="p-4"
                icon={<BellIcon />}
                onClick={() => setIsNotificationOpen(true)}
              />
              {unreadCount > 0 && (
                <span className="notification-badge position-absolute rounded-circle bg-danger text-white fw-semibold flex-center fs-xxs lh-1 ratio-1">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </div>
            {/* ─── Notification Panel ─── */}

            <NotificationPanel
              isOpen={isNotificationOpen}
              onClose={() => setIsNotificationOpen(false)}
            />

            <Dropdown align="end" className="profile-dropdown">
              <Dropdown.Toggle
                id="user-info-toggle"
                variant="light"
                className={`d-flex align-items-center gap-4 border-0 bg-transparent shadow-none p-0 lh-sm`}
              >
                <Image
                  src="/assets/user.png"
                  alt="User Profile"
                  width={38}
                  height={38}
                  className="object-fit-cover rounded-3 ratio-1"
                />
                <div className="text-start">
                  <p className="fw-medium mb-2 text-primary">{user?.name}</p>
                  <span className="text-muted fs-xs text-secondary">
                    {user?.role_relation?.name?.toUpperCase()}
                  </span>
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu className="shadow-sm dropdown-menu-end border p-3 z-1022">
                <Dropdown.Item
                  as="button"
                  className="rounded-2 mb-2"
                  onClick={() => navigate("/profile")}
                >
                  Profile
                </Dropdown.Item>

                <hr className="dropdown-divider my-2" />
                <Dropdown.Item
                  as="button"
                  className="text-danger rounded-2 "
                  onClick={handleLogout}
                >
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </header>

        <div
          className="flex-grow-1 overflow-hidden position-relative"
          key={user?.current_organization_id}
        >
          {isSwitchingOrg ? (
            // Switching organisation: a page-shaped skeleton until the new organisation's data loads
            <PageSkeleton title className="px-12 py-8 overflow-hidden" />
          ) : (
            children
          )}
        </div>
      </main>

      {activeModal === "followup" && (
        <FollowUpModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
          onSave={() => {
            setActiveModal(null);
            window.location.reload();
          }}
          className="overflow-hidden"
          centered={false}
          // dialogClassName="me-3 mt-3 mb-4"
          title={"Create Follow-up"}
          size="xl"
        />
      )}
      {activeModal === "customer" && (
        <CustomerModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
          className="overflow-hidden"
          onSave={() => {
            setActiveModal(null);
            window.location.reload();
          }}
          centered={false}
          // dialogClassName="me-3 mt-3 mb-4"
          title={"Create Customer"}
          size="xl"
        />
      )}
    </div>
  );
};

export default Layout;
